import { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  InputBase,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import LinearProgress from '@mui/material/LinearProgress';
import { ShButton } from 'shared/SharedStyles/ShInputs';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Visibility';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import { format, parseISO, isValid } from 'date-fns';
import { EmailCampaignTableProps } from './EmailCampaignModels';

const statusChip = (status: string) => {
  switch (status.toLowerCase()) {
    case 'sent':
      return <Chip icon={<CheckCircleIcon sx={{ color: 'success.main' }} />} label="Sent" color="success" size="small" variant="outlined" />;
    case 'draft':
      return <Chip icon={<AccessTimeIcon sx={{ color: 'warning.main' }} />} label="Draft" color="warning" size="small" variant="outlined" />;
    case 'failed':
      return <Chip icon={<ErrorIcon sx={{ color: 'error.main' }} />} label="Failed" color="error" size="small" variant="outlined" />;
    default:
      return <Chip label={status} size="small" variant="outlined" />;
  }
};

const formatDate = (date: any) => {
  if (!date) return '';
  let d = typeof date === 'string' ? parseISO(date) : new Date(date);
  if (!isValid(d)) return '';
  return format(d, 'MMM d, yyyy h:mm a');
};

export const EmailCampaignTable = ({ jobId, campaigns, onPreview, sendNowButtonProps, fetchCampaignsStatus, onDelete, onSendNowConfirm, onEdit }: EmailCampaignTableProps) => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('Name (A - Z)');
  const [type, setType] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  // Filter campaigns by jobId, search/type, and status
  const filteredCampaigns = campaigns.filter(
    (c) => String(c.jobId) === String(jobId) &&
      (type === 'All' || c.type === type) &&
      (statusFilter === 'All' || c.status?.toLowerCase() === statusFilter.toLowerCase()) &&
      (search === '' || c.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Paper variant="outlined" sx={{ mt: 4 }}>
      <Box p={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">Campaigns</Typography>
            <Select
              value={sort}
              onChange={e => setSort(e.target.value)}
              size="small"
              variant="outlined"
              displayEmpty
              sx={{ minWidth: 160 }}
              IconComponent={ArrowDropDownIcon}
            >
              <MenuItem value="Name (A - Z)">Name (A - Z)</MenuItem>
              <MenuItem value="Name (Z - A)">Name (Z - A)</MenuItem>
              <MenuItem value="Date Created Desc">Date Created Desc</MenuItem>
              <MenuItem value="Date Created Asc">Date Created Asc</MenuItem>
            </Select>
            <Select
              value={type}
              onChange={e => setType(e.target.value)}
              size="small"
              variant="outlined"
              displayEmpty
              sx={{ minWidth: 100 }}
              IconComponent={ArrowDropDownIcon}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Regular">Regular</MenuItem>
              <MenuItem value="Sequence">Sequence</MenuItem>
            </Select>
            <Select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              size="small"
              variant="outlined"
              displayEmpty
              sx={{ minWidth: 120 }}
              IconComponent={ArrowDropDownIcon}
            >
              <MenuItem value="All">All Statuses</MenuItem>
              <MenuItem value="Draft">Draft</MenuItem>
              <MenuItem value="Sent">Sent</MenuItem>
            </Select>
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: 2, px: 1 }}>
            <InputBase
              placeholder="Search by title..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
              inputProps={{ 'aria-label': 'search campaigns' }}
            />
            <IconButton size="small" disabled>
              <SearchIcon />
            </IconButton>
          </Box>
        </Stack>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell >Campaigns</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date Created</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell >Recipients</TableCell>
                <TableCell >Successful Sends</TableCell>
                <TableCell >Failed Sends</TableCell>
                <TableCell >Status</TableCell>
                <TableCell >Actions</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fetchCampaignsStatus === 'pending' && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <LinearProgress sx={{ width: '100%' }} />
                  </TableCell>
                </TableRow>
              )}
              {filteredCampaigns.length === 0 && fetchCampaignsStatus !== 'pending' ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" spacing={1}>
                      <MailOutlineIcon color="primary" sx={{ fontSize: 48 }} />
                      <Typography color="text.secondary">No campaigns found</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCampaigns.map((row, idx) => (
                  <TableRow
                    key={row.id}
                    sx={{ backgroundColor: idx % 2 === 0 ? 'background.paper' : 'grey.50' }}
                  >
                    <TableCell>
                      <Box>
                        <Typography >{row.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{row.subject}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    <TableCell>{row.sentAt ? formatDate(row.sentAt) : '-'}</TableCell>
                    <TableCell>{row.recipientsCount}</TableCell>
                    <TableCell>{row.successfulSends ?? 0}</TableCell>
                    <TableCell>{row.failedSends ?? 0}</TableCell>
                    <TableCell>
                      {statusChip(row.status)}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {row.status === 'Draft' ? (
                        <ShButton disableElevation size="small" variant="contained" color="primary" onClick={onSendNowConfirm ? () => onSendNowConfirm(row.id) : () => {}} startIcon={<SendIcon />} {...sendNowButtonProps}>
                          Send Now
                        </ShButton>
                      ) : row.status === 'Sent' ? (
                        <Typography variant="caption" color="success.main">Sent</Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <ShButton disableElevation size="small" variant="text" color="info" startIcon={<PreviewIcon />} onClick={() => onPreview && onPreview(row)}>
                        Preview
                      </ShButton>
                      <ShButton disableElevation size="small" variant="text" color="primary" startIcon={<EditIcon />} 
                        onClick={() => {
                          if (row.status?.toLowerCase().trim() === 'sent') return;
                          onEdit && onEdit(row);
                        }}
                        disabled={row.status?.toLowerCase().trim() === 'sent'}>
                        Edit
                      </ShButton>
                      <ShButton disableElevation size="small" variant="text" color="error"
                        onClick={() => {
                          if (row.status?.toLowerCase().trim() === 'sent') return;
                          onDelete && onDelete(row.id);
                        }}
                        startIcon={<DeleteIcon />} disabled={row.status?.toLowerCase().trim() === 'sent'}>
                        Delete
                      </ShButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
}; 